import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock } from 'lucide-react';
import MarketingNav from '@/components/layout/MarketingNav';
import MarketingFooter from '@/components/layout/MarketingFooter';
import { BLOG_POSTS } from '@/lib/blogPosts';
import Seo from '@/components/Seo';

const ACCENT = {
  cyan: 'text-boosta-cyan bg-boosta-cyan/10 border-boosta-cyan/25',
  yellow: 'text-boosta-yellow bg-boosta-yellow/10 border-boosta-yellow/25',
  emerald: 'text-boosta-emerald bg-boosta-emerald/10 border-boosta-emerald/25',
};

export default function Blog() {
  const [featured, ...rest] = BLOG_POSTS;
  return (
    <div className="bg-boosta-ink min-h-screen text-white">
      <Seo title="Boosta Blog — AI Advertising Playbooks & Growth Resources"
        description="Playbooks, teardowns and operator thinking on AI ad creative, Meta strategy, A/B testing and growth economics — from the Boosta team." />
      <MarketingNav />
      <div className="relative pt-32 pb-24 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0 grain" />
        <div className="relative max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-4">Resources</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-3d max-w-3xl" data-testid="blog-heading">
              Field notes from the <span className="gradient-text">AI advertising</span> frontier.
            </h1>
            <p className="mt-5 text-white/55 max-w-2xl text-lg">Playbooks, teardowns and operator thinking on AI creative, paid social and growth economics.</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.6 }} className="mt-14">
            <Link to={`/blog/${featured.slug}`} data-testid="blog-featured"
              className="group block glass glass-hover rounded-3xl p-8 md:p-12 relative overflow-hidden">
              <div className="absolute -inset-8 bg-gradient-to-br from-boosta-cyan/10 via-transparent to-boosta-yellow/10 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-full border ${ACCENT[featured.accent]}`}>{featured.category}</span>
                  <span className="text-xs text-white/40 flex items-center gap-1.5"><Clock className="w-3 h-3" />{featured.readTime}</span>
                </div>
                <h2 className="font-display font-black text-2xl md:text-4xl tracking-tight max-w-3xl group-hover:text-boosta-cyan transition-colors">{featured.title}</h2>
                <p className="mt-4 text-white/55 max-w-2xl leading-relaxed">{featured.excerpt}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-boosta-yellow">
                  Read the playbook <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </motion.div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            {rest.map((p, i) => (
              <motion.div key={p.slug} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }}>
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                  className="group flex flex-col h-full glass glass-hover rounded-2xl p-7">
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-full border ${ACCENT[p.accent]}`}>{p.category}</span>
                    <span className="text-xs text-white/40 flex items-center gap-1.5"><Clock className="w-3 h-3" />{p.readTime}</span>
                  </div>
                  <h3 className="font-display font-bold text-xl tracking-tight group-hover:text-boosta-cyan transition-colors">{p.title}</h3>
                  <p className="mt-3 text-sm text-white/50 leading-relaxed flex-1">{p.excerpt}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-xs font-bold text-white/60 group-hover:text-boosta-yellow transition-colors">
                    Read article <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      <MarketingFooter />
    </div>
  );
}
